import React, { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/api";
import { PageTitle, EmptyState, Badge } from "@/components/ui-kit";
import InspireBanner from "@/components/InspireBanner";
import { downloadFile, uploadFile } from "@/lib/download";
import { School, Plus, Pencil, Trash2, FileDown, FileUp, FileText, Clock, Users, HandHeart, LogIn, Search, AlertTriangle, UserCog } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { playSuccess } from "@/lib/sound";

const empty = { name: "", stage: "", type: "بنين", authority: "حكومي", email: "", phone: "", coordinator_name: "", students_count: 0, staff_count: 0 };
const AUTHS = [{ k: "", l: "كل المدارس" }, { k: "حكومي", l: "حكومي" }, { k: "أهلي", l: "أهلي" }, { k: "عالمي وأجنبي", l: "عالمي وأجنبي" }];
const authMatch = (a, k) => !k || (k === "عالمي وأجنبي" ? ((a || "").includes("عالمي") || (a || "").includes("أجنبي")) : (a || "حكومي") === k);
// كشف الفجوات: مدارس بلا منسق / متطوعون بلا فرص / فرص بلا ساعات مسجّلة
const GAPS = [
  { k: "no_coord", l: "بلا منسق", test: (s) => !s.coordinator_name },
  { k: "vol_no_opp", l: "متطوعون بلا فرص", test: (s) => (s.stats?.volunteers || 0) > 0 && (s.stats?.opportunities || 0) === 0 },
  { k: "opp_no_hours", l: "فرص بلا ساعات", test: (s) => (s.stats?.opportunities || 0) > 0 && (s.stats?.hours || 0) === 0 },
  { k: "inactive", l: "بلا نشاط", test: (s) => (s.stats?.opportunities || 0) === 0 && (s.stats?.volunteers || 0) === 0 },
];
const schoolGaps = (s) => GAPS.filter((g) => g.k !== "inactive" && g.test(s)).map((g) => g.l);
const F = ({ label, children }) => (<div><label className="text-xs text-stone-500 mb-1 block">{label}</label>{children}</div>);
const inputCls = "w-full px-3 py-2 rounded-lg bg-amber-50 border border-amber-400/30 text-stone-800 text-sm focus:outline-none focus:border-amber-400";

export default function Schools() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [delId, setDelId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState("");
  const [authF, setAuthF] = useState("");
  const [gapF, setGapF] = useState("");
  const [coordEdit, setCoordEdit] = useState(null);
  const [coordForm, setCoordForm] = useState({ coordinator_name: "", email: "", phone: "" });

  const { impersonate } = useAuth();
  const load = () => { api.get("/schools").then((r) => setItems(r.data)).catch(() => {}); };
  useEffect(load, []);

  const filtered = items.filter((s) =>
    (!typeF || s.type === typeF) &&
    authMatch(s.authority, authF) &&
    (!gapF || GAPS.find((g) => g.k === gapF)?.test(s)) &&
    (!q || (s.name || "").includes(q) || (s.coordinator_name || "").includes(q))
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...empty, ...s }); setEditId(s.id); setOpen(true); };
  const openCoord = (s) => { setCoordForm({ coordinator_name: s.coordinator_name || "", email: s.email || "", phone: s.phone || "" }); setCoordEdit(s); };
  const saveCoord = async () => {
    if (!coordForm.coordinator_name || !coordForm.email || !coordForm.phone) return toast.error("اسم المنسق والبريد والجوال مطلوبة");
    try {
      await api.put(`/schools/${coordEdit.id}`, { ...coordEdit, ...coordForm });
      playSuccess();
      toast.success("تم حفظ بيانات المنسق وربطه بالمدرسة وتحديث دخوله");
      setCoordEdit(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    if (!form.name) return toast.error("اسم المدرسة مطلوب");
    try {
      if (editId) await api.put(`/schools/${editId}`, form);
      else await api.post("/schools", form);
      playSuccess();
      toast.success(editId ? "تم التحديث" : "تمت إضافة المدرسة");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async () => {
    try { await api.delete(`/schools/${delId}`); toast.success("تم حذف المدرسة"); setDelId(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const doExport = async () => { try { await downloadFile("/schools/export/all", "schools.xlsx"); } catch { toast.error("تعذر التصدير"); } };
  const doTemplate = async () => { try { await downloadFile("/schools/import/template", "schools_template.xlsx"); } catch { toast.error("تعذر تحميل القالب"); } };
  const fileRef = useRef();
  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadFile("/schools/import/preview", file);
      setPendingFile(file);
      setPreview(res);
    } catch (err) { toast.error(err.message || "تعذر قراءة الملف"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  const confirmImport = async () => {
    if (!pendingFile) return;
    setImporting(true);
    try {
      const res = await uploadFile("/schools/import", pendingFile);
      toast.success(`تم استيراد ${res.added} مدرسة جديدة، وتحديث ${res.updated || 0}، وإنشاء ${res.coordinators || 0} حساب منسق`);
      setPreview(null); setPendingFile(null); load();
    } catch (err) { toast.error(err.message || "تعذر الاستيراد"); }
    finally { setImporting(false); }
  };

  return (
    <div>
      <InspireBanner />

      <PageTitle title="المدارس" subtitle="إدارة مدارس منطقة حائل" icon={School}
        actions={<>
          <button data-testid="template-schools-btn" onClick={doTemplate} className="glass glass-hover px-3 py-2 rounded-xl flex items-center gap-2 text-stone-800 text-sm"><FileText size={17} /> قالب Excel</button>
          <button data-testid="export-schools-btn" onClick={doExport} className="glass glass-hover px-3 py-2 rounded-xl flex items-center gap-2 text-stone-800 text-sm"><FileDown size={17} /> تصدير Excel</button>
          <button data-testid="import-schools-btn" onClick={() => fileRef.current?.click()} className="glass glass-hover px-3 py-2 rounded-xl flex items-center gap-2 text-stone-800 text-sm"><FileUp size={17} /> استيراد Excel</button>
          <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={doImport} />
          <button data-testid="add-school-btn" onClick={openNew} className="btn-gold px-4 py-2 rounded-xl flex items-center gap-2"><Plus size={18} /> مدرسة جديدة</button>
        </>}
      />

      <div className="glass rounded-2xl p-4 mb-5 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
          <input data-testid="school-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث باسم المدرسة أو المنسق..." className={`${inputCls} pr-10`} />
        </div>
        <div className="flex gap-2">
          {[{ k: "", l: `الكل (${items.length})` }, { k: "بنين", l: `بنين (${items.filter((s) => s.type === "بنين").length})` }, { k: "بنات", l: `بنات (${items.filter((s) => s.type === "بنات").length})` }].map((f) => (
            <button key={f.k} data-testid={`school-filter-${f.k || "all"}`} onClick={() => setTypeF(f.k)} className={`px-4 py-2 rounded-xl text-sm transition ${typeF === f.k ? "btn-gold" : "glass glass-hover text-stone-600"}`}>{f.l}</button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap w-full sm:w-auto border-t sm:border-t-0 sm:border-r border-amber-400/20 sm:pr-3 pt-3 sm:pt-0">
          {AUTHS.map((f) => (
            <button key={f.k} data-testid={`school-auth-${f.k || "all"}`} onClick={() => setAuthF(f.k)} className={`px-4 py-2 rounded-xl text-sm transition ${authF === f.k ? "bg-sky-600 text-white" : "glass glass-hover text-stone-600"}`}>{f.l} {f.k ? `(${items.filter((s) => authMatch(s.authority, f.k)).length})` : ""}</button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap w-full border-t border-amber-400/20 pt-3 items-center">
          <span className="text-xs text-stone-500 flex items-center gap-1"><AlertTriangle size={14} className="text-amber-600" /> تدقيق الفجوات:</span>
          <button data-testid="gap-filter-all" onClick={() => setGapF("")} className={`px-3 py-1.5 rounded-xl text-xs transition ${gapF === "" ? "btn-gold" : "glass glass-hover text-stone-600"}`}>الكل</button>
          {GAPS.map((g) => {
            const c = items.filter((s) => g.test(s)).length;
            return (
              <button key={g.k} data-testid={`gap-filter-${g.k}`} onClick={() => setGapF(gapF === g.k ? "" : g.k)} className={`px-3 py-1.5 rounded-xl text-xs transition ${gapF === g.k ? "bg-red-600 text-white" : "glass glass-hover text-stone-600"}`}>{g.l} ({c})</button>
            );
          })}
        </div>
      </div>

      {!filtered.length ? <EmptyState text="لا توجد مدارس مطابقة" icon={School} /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((s) => (
            <div key={s.id} className="glass glass-hover rounded-2xl p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-head font-bold text-stone-800 text-lg">{s.name}</h4>
                  <p className="text-xs text-stone-500 mt-1">{s.stage} • {s.coordinator_name || "لا يوجد منسق"}</p>
                </div>
                <Badge className="bg-amber-500/15 text-amber-700 border-amber-400/30">{s.type}</Badge>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-stone-500">
                <span>السلطة: <b className="text-sky-700">{s.authority || "حكومي"}</b></span>
                <span>الطلاب: <b className="text-stone-700">{s.students_count ?? 0}</b></span>
                {s.phone && <span>الجوال: <b className="text-stone-700" dir="ltr">{s.phone}</b></span>}
                {s.email && <span className="truncate max-w-full">البريد: <b className="text-stone-700" dir="ltr">{s.email}</b></span>}
              </div>
              {schoolGaps(s).length > 0 && (
                <div data-testid={`school-gaps-${s.id}`} className="flex flex-wrap gap-1.5 mt-2">
                  {schoolGaps(s).map((gl) => (
                    <span key={gl} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/10 text-red-700 text-[10px] border border-red-400/30"><AlertTriangle size={11} />{gl}</span>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div className="bg-amber-100/60 rounded-lg py-2"><div className="text-amber-600 font-bold flex items-center justify-center gap-1"><HandHeart size={14} />{s.stats.opportunities}</div><div className="text-[10px] text-stone-500">فرص</div></div>
                <div className="bg-amber-100/60 rounded-lg py-2" title="المتطوعون الفعليّون (من لهم مشاركات وساعات مسجّلة)"><div className="text-amber-600 font-bold flex items-center justify-center gap-1"><Users size={14} />{s.stats.real_volunteers ?? 0}</div><div className="text-[10px] text-stone-500">متطوع فعلي</div></div>
                <div className="bg-amber-100/60 rounded-lg py-2"><div className="text-amber-600 font-bold flex items-center justify-center gap-1"><Clock size={14} />{s.stats.hours}</div><div className="text-[10px] text-stone-500">ساعة</div></div>
              </div>
              <div className="flex gap-2 mt-4">
                <button data-testid={`impersonate-${s.id}`} onClick={() => impersonate(s.id)} className="flex-1 glass glass-hover py-2 rounded-lg text-sky-700 text-sm flex items-center justify-center gap-1"><LogIn size={14} /> الدخول كمنسّق للمدرسة</button>
                <button data-testid={`edit-school-${s.id}`} onClick={() => openEdit(s)} className="flex-1 glass glass-hover py-2 rounded-lg text-amber-700 text-sm flex items-center justify-center gap-1"><Pencil size={14} /> تعديل</button>
                <button data-testid={`del-school-${s.id}`} onClick={() => setDelId(s.id)} className="px-3 py-2 rounded-lg bg-red-500/15 border border-red-400/30 text-red-700"><Trash2 size={15} /></button>
              </div>
              <button data-testid={`edit-coord-${s.id}`} onClick={() => openCoord(s)} className="w-full mt-2 py-2 rounded-lg bg-emerald-500/12 border border-emerald-400/30 text-emerald-700 text-sm flex items-center justify-center gap-1 hover:bg-emerald-500/20 transition"><UserCog size={14} /> {s.coordinator_name ? "تعديل بيانات المنسق" : "تعيين منسق للمدرسة"}</button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!coordEdit} onOpenChange={(o) => { if (!o) setCoordEdit(null); }}>
        <DialogContent className="glass border-amber-400/30 text-stone-800 max-w-md" dir="rtl">
          <DialogHeader><DialogTitle className="gold-text font-head">منسق: {coordEdit?.name}</DialogTitle></DialogHeader>
          <p className="text-[11px] text-sky-800 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2">هذه بيانات دخول المنسق. الحفظ يربطه بالمدرسة ويُحدّث حساب دخوله فوراً — يدخل من بوابة «منسق التطوع» بالبريد + الجوال.</p>
          <div className="grid grid-cols-1 gap-3">
            <F label="اسم المنسق"><input data-testid="coord-name" value={coordForm.coordinator_name} onChange={(e) => setCoordForm((f) => ({ ...f, coordinator_name: e.target.value }))} className={inputCls} /></F>
            <F label="البريد الإلكتروني (للدخول)"><input data-testid="coord-email" value={coordForm.email} onChange={(e) => setCoordForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} dir="ltr" placeholder="example@edu.sa" /></F>
            <F label="جوال المنسق (للدخول)"><input data-testid="coord-phone" value={coordForm.phone} onChange={(e) => setCoordForm((f) => ({ ...f, phone: e.target.value.replace(/[^0-9+]/g, "") }))} className={inputCls} dir="ltr" placeholder="05xxxxxxxx" inputMode="numeric" /></F>
          </div>
          <DialogFooter><button data-testid="save-coord-btn" onClick={saveCoord} className="btn-gold px-6 py-2 rounded-xl">حفظ وربط بالمدرسة</button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border-amber-400/30 text-stone-800 max-w-lg" dir="rtl">
          <DialogHeader><DialogTitle className="gold-text font-head">{editId ? "تعديل المدرسة" : "مدرسة جديدة"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="اسم المدرسة"><input data-testid="school-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></F>
            <F label="المرحلة"><input value={form.stage} onChange={(e) => set("stage", e.target.value)} className={inputCls} placeholder="ابتدائية / متوسطة / ثانوية" /></F>
            <F label="النوع"><select data-testid="school-type" value={form.type} onChange={(e) => set("type", e.target.value)} className={inputCls}><option value="بنين">بنين</option><option value="بنات">بنات</option></select></F>
            <F label="السلطة"><select data-testid="school-authority" value={form.authority || "حكومي"} onChange={(e) => set("authority", e.target.value)} className={inputCls}><option value="حكومي">حكومي</option><option value="أهلي">أهلي</option><option value="عالمي وأجنبي">عالمي وأجنبي</option></select></F>
            <div className="sm:col-span-2 text-[11px] text-sky-800 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2">بيانات المنسق أدناه (الاسم + البريد + الجوال) هي نفسها بيانات دخوله. أي تعديل هنا يُحدّث حساب دخول المنسق مباشرة، ويدخل من بوابة «منسق التطوع» بالبريد + الجوال.</div>
            <F label="اسم المنسق"><input data-testid="school-coord-name" value={form.coordinator_name} onChange={(e) => set("coordinator_name", e.target.value)} className={inputCls} /></F>
            <F label="البريد الإلكتروني للمنسق (للدخول)"><input data-testid="school-email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="example@edu.sa" dir="ltr" /></F>
            <F label="جوال المنسق (للدخول)"><input data-testid="school-coord-phone" value={form.phone} onChange={(e) => set("phone", e.target.value.replace(/[^0-9+]/g, ""))} className={inputCls} placeholder="05xxxxxxxx" dir="ltr" /></F>
            <F label="إجمالي عدد الطلاب"><input data-testid="school-students" type="number" value={form.students_count} onChange={(e) => set("students_count", e.target.value)} className={inputCls} /></F>
            <F label="إجمالي عدد منسوبي المدرسة"><input data-testid="school-staff" type="number" value={form.staff_count} onChange={(e) => set("staff_count", e.target.value)} className={inputCls} /></F>
          </div>
          <DialogFooter><button data-testid="save-school-btn" onClick={save} className="btn-gold px-6 py-2 rounded-xl">حفظ</button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent className="glass border-amber-400/30 text-stone-800" dir="rtl">
          <AlertDialogHeader><AlertDialogTitle className="text-stone-800">تأكيد حذف المدرسة</AlertDialogTitle><AlertDialogDescription className="text-stone-500">سيتم حذف المدرسة وجميع بياناتها (المنسق، المتطوعون، الفرص، الأنشطة، الصور) نهائياً.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="bg-amber-50 border-amber-400/30 text-stone-800">إلغاء</AlertDialogCancel><AlertDialogAction data-testid="confirm-del-school" onClick={remove} className="bg-red-600 hover:bg-red-700">حذف نهائي</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) { setPreview(null); setPendingFile(null); } }}>
        <DialogContent className="glass border-amber-400/30 text-stone-800 max-w-3xl" dir="rtl">
          <DialogHeader><DialogTitle className="gold-text font-head">معاينة استيراد المدارس</DialogTitle></DialogHeader>
          {preview && (
            <>
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-700">جديدة: {preview.new}</span>
                <span className="px-3 py-1 rounded-full bg-amber-500/15 text-amber-700">مكررة (ستُتخطى): {preview.duplicates}</span>
                <span className="px-3 py-1 rounded-full bg-sky-500/15 text-sky-700">الإجمالي: {preview.total}</span>
              </div>
              <div className="max-h-80 overflow-y-auto mt-3">
                <table className="w-full text-xs">
                  <thead><tr className="text-stone-500 text-right border-b border-amber-400/15">
                    <th className="py-2 px-2 font-medium">المدرسة</th><th className="py-2 px-2 font-medium">النوع</th><th className="py-2 px-2 font-medium">المنسق</th><th className="py-2 px-2 font-medium">الطلاب</th><th className="py-2 px-2 font-medium">الحالة</th><th className="py-2 px-2 font-medium">ملاحظات</th>
                  </tr></thead>
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={i} data-testid={`preview-row-${i}`} className="border-b border-amber-400/10">
                        <td className="py-2 px-2 text-stone-800 font-medium">{r.name}</td>
                        <td className="py-2 px-2 text-stone-600">{r.type}</td>
                        <td className="py-2 px-2 text-stone-600">{r.coordinator_name || "-"}</td>
                        <td className="py-2 px-2 text-stone-600">{r.students_count}</td>
                        <td className="py-2 px-2">{r.status === "duplicate" ? <span className="text-amber-700">مكررة</span> : <span className="text-emerald-700">جديدة</span>}</td>
                        <td className="py-2 px-2 text-stone-500">{r.errors.length ? r.errors.join("، ") : "—"}</td>
                      </tr>
                    ))}
                    {!preview.rows.length && <tr><td colSpan={6} className="py-6 text-center text-stone-500">لا توجد صفوف صالحة في الملف</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <DialogFooter>
            <button data-testid="cancel-import-btn" onClick={() => { setPreview(null); setPendingFile(null); }} className="glass glass-hover px-5 py-2 rounded-xl text-stone-700">إلغاء</button>
            <button data-testid="confirm-import-btn" onClick={confirmImport} disabled={importing || !preview?.new} className="btn-gold px-6 py-2 rounded-xl disabled:opacity-50">{importing ? "جارٍ الاستيراد..." : `تأكيد استيراد ${preview?.new || 0} مدرسة`}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
